import { prefersReducedMotion, isLowPowerDevice } from '@theme/utilities';

const REVEALED_CLASS = 'is-revealed';
const REVEAL_SELECTOR = '[data-reveal]';

/**
 * Progressive-enhancement scroll reveal for sections and layout elements.
 *
 * Elements opt in with a `data-reveal` attribute (see snippets/reveal-attrs.liquid).
 * The hidden initial state only applies under `body.motion-js`, which this module
 * adds after pre-revealing everything already in the viewport — so content stays
 * visible when JS never runs, and the first screen never re-animates after load.
 */
class ScrollReveal {
  #observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add(REVEALED_CLASS);
        this.#observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  start() {
    const viewportBottom = window.innerHeight;
    for (const element of document.querySelectorAll(REVEAL_SELECTOR)) {
      if (element.getBoundingClientRect().top < viewportBottom) {
        element.classList.add(REVEALED_CLASS);
      }
    }

    document.body.classList.add('motion-js');
    this.observe();

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element) this.observe(event.target);
    });
  }

  /** @param {Element | Document} root */
  observe(root = document) {
    const pending = root.querySelectorAll(`${REVEAL_SELECTOR}:not(.${REVEALED_CLASS})`);
    for (const element of pending) {
      this.#observer.observe(element);
    }
    if (root instanceof Element && root.matches(REVEAL_SELECTOR) && !root.classList.contains(REVEALED_CLASS)) {
      this.#observer.observe(root);
    }
  }
}

if (!prefersReducedMotion() && !isLowPowerDevice() && 'IntersectionObserver' in window) {
  new ScrollReveal().start();
}
